import { uid } from '../../../../lib/uid';
import type { FileCategory } from '../fileTypes';

/**
 * Test-data generator backing the Autogenerate button on the upload form.
 *
 * Values are synthetic. Never put a real person's name, a real account number, or anything
 * drawn from production in here — a generated file is uploaded and stored like any other,
 * so whatever is written here ends up in the file store.
 */

/**
 * The one extension this can honestly produce.
 *
 * Every other category takes a binary format — PNG, JPEG, PDF — and the server checks magic
 * bytes, not just the name. Writing a byte header by hand to satisfy that check would be
 * fabricating a file that claims to be an image and is not, which is precisely what the
 * check exists to catch. CSV is the exception because it genuinely is text: the server's
 * only structural rule for it is that it carries no NUL byte.
 */
const GENERATABLE_EXTENSION = '.csv';

const UNITS = ['Operations', 'Finance', 'Logistics', 'Records'];

/**
 * The category a sample file can be produced for, or null when none can.
 *
 * Read from the categories the **server** sent rather than assumed: the registry decides
 * what exists and what each accepts, and a hard-coded key here would break silently the day
 * one is renamed.
 */
export function generatableCategory(categories: readonly FileCategory[]): FileCategory | null {
  return (
    categories.find((category) =>
      category.allowedExtensions.some((ext) => ext.toLowerCase() === GENERATABLE_EXTENSION),
    ) ?? null
  );
}

/**
 * A small, valid CSV for that category.
 *
 * Built as a real `File`, not a path or a name, because that is what the form's own file
 * input would have handed over — the rest of the form cannot tell the difference, and
 * neither can the request.
 */
export function sampleUploadFile(): File {
  const suffix = uid().slice(0, 6).toLowerCase();

  const rows = [
    'code,name,order',
    ...UNITS.map((unit, index) => `${String(index + 1).padStart(4, '0')},${unit} ${suffix},${(index + 1) * 10}`),
  ];

  // The trailing newline is what a text export actually produces; leaving it off is the
  // small way generated data stops looking like real data.
  const content = `${rows.join('\n')}\n`;

  return new File([content], `sample-import-${suffix}.csv`, { type: 'text/csv' });
}
